import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchPassMappingSuggestions } from '../passNameMappingsClient'
import { buildPassMappings, type PassMappingHeuristicMode } from './buildPassMappings'
import { countLearnedPrefills } from './passMappingAutofill'
import { uniquePassRawsFromMappingKeys } from './passMappingKeys'

export interface UsePassMappingAutofillOptions {
  passNames: string[]
  wpsPassLabels: string[]
  heuristicMode?: PassMappingHeuristicMode
  enabled?: boolean
  /** Weld-specific mappings that take priority over platform suggestions. */
  initialOverrides?: Record<string, string>
}

interface PassMappingAutofillCache {
  fetchKey: string
  passMappings: Record<string, string>
  learnedSuggestions: Record<string, string>
}

export function usePassMappingAutofill({
  passNames,
  wpsPassLabels,
  heuristicMode = 'telemetry',
  enabled = true,
  initialOverrides
}: UsePassMappingAutofillOptions) {
  const [passMappings, setPassMappings] = useState<Record<string, string>>({})
  const [learnedSuggestions, setLearnedSuggestions] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const cacheRef = useRef<PassMappingAutofillCache | null>(null)

  const passNamesKey = passNames.join('\0')
  const wpsLabelsKey = wpsPassLabels.join('\0')
  const overridesKey = initialOverrides ? JSON.stringify(initialOverrides) : ''
  const fetchKey = [passNamesKey, wpsLabelsKey, heuristicMode, overridesKey].join('|')

  useEffect(() => {
    if (!enabled) {
      cacheRef.current = null
      setPassMappings({})
      setLearnedSuggestions({})
      setLoading(false)
      return
    }

    if (passNames.length === 0) {
      setPassMappings({})
      setLearnedSuggestions({})
      setLoading(false)
      return
    }

    const cached = cacheRef.current
    if (cached?.fetchKey === fetchKey) {
      setPassMappings(cached.passMappings)
      setLearnedSuggestions(cached.learnedSuggestions)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setPassMappings({})
    setLearnedSuggestions({})

    void (async () => {
      let globalSuggestions: Record<string, string> = {}
      try {
        globalSuggestions = await fetchPassMappingSuggestions({
          rawPassNames: uniquePassRawsFromMappingKeys(passNames),
          wpsPassLabels
        })
      } catch {
        globalSuggestions = {}
      }
      if (cancelled) return

      const built = buildPassMappings({
        passNames,
        wpsPassLabels,
        globalSuggestions,
        heuristicMode
      })

      const labelSet = new Set(wpsPassLabels)
      const merged = { ...built }
      if (initialOverrides) {
        for (const [raw, canonical] of Object.entries(initialOverrides)) {
          if (canonical && labelSet.has(canonical)) {
            merged[raw] = canonical
          }
        }
      }

      const learned = globalSuggestions ?? {}
      cacheRef.current = {
        fetchKey,
        passMappings: merged,
        learnedSuggestions: learned
      }
      setLearnedSuggestions(learned)
      setPassMappings(merged)
      setLoading(false)
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fetchKey, passNames, wpsPassLabels, heuristicMode, initialOverrides])

  const learnedPrefillCount = useMemo(
    () => countLearnedPrefills(passNames, passMappings, learnedSuggestions, wpsPassLabels),
    [passNames, passMappings, learnedSuggestions, wpsPassLabels]
  )

  return {
    passMappings,
    setPassMappings,
    loading,
    learnedSuggestions,
    learnedPrefillCount,
    hasLearnedPrefill: learnedPrefillCount > 0
  }
}
